import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  ArrowUpRight,
  Building2,
  BusFront,
  Cog,
  HeartPulse,
  MapPin,
  Route,
  Users,
  Wind,
  Zap,
} from "lucide-react";
import { useRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { advancingSlogan, projects } from "../data/content";
import { ResponsiveImage } from "./ResponsiveImage";
import { areaQuantity, areaValue, projectValue } from "../lib/transparency";
import { formatSummaryCurrency } from "../lib/format";
import type { Project } from "../types/content";

gsap.registerPlugin(useGSAP, ScrollTrigger);

interface FrontCard {
  slug: Project['area'];
  label: string;
  title: string;
  description: string;
  href: string;
  mediaSlug: string;
  icon: ReactNode;
  tone: string;
  metrics: { icon: ReactNode; text: string }[];
}

const fronts: FrontCard[] = [
  {
    slug: "saude",
    label: "Saúde",
    title: "Mais estrutura para cuidar das pessoas.",
    description: "Projetos de Unidades Básicas de Saúde e veículos apresentados para o atendimento em Palmas.",
    href: "/saude",
    mediaSlug: "ubs",
    icon: <HeartPulse aria-hidden="true" />,
    tone: "blue",
    metrics: [
      { icon: <Building2 aria-hidden="true" />, text: `${areaQuantity(projects, 'saude', 'units')} futuras UBS` },
      { icon: <BusFront aria-hidden="true" />, text: `${areaQuantity(projects, 'saude', 'vehicles')} veículos` },
    ],
  },
  {
    slug: "infraestrutura",
    label: "Infraestrutura",
    title: "Mobilidade para a cidade e o interior.",
    description: "Pavimentação urbana e rural, máquinas e equipamentos apresentados para bairros e comunidades.",
    href: "/infraestrutura",
    mediaSlug: "asfalto-pedras",
    icon: <Route aria-hidden="true" />,
    tone: "green",
    metrics: [
      { icon: <Route aria-hidden="true" />, text: `${areaQuantity(projects, 'infraestrutura', 'streets')} ruas` },
      { icon: <MapPin aria-hidden="true" />, text: `${areaQuantity(projects, 'infraestrutura', 'roadLengthMeters')} m` },
      { icon: <Cog aria-hidden="true" />, text: `${areaQuantity(projects, 'infraestrutura', 'machines')} máquinas e caminhões` },
    ],
  },
  {
    slug: "grandes-obras",
    label: "Grandes obras",
    title: "Estrutura para novos caminhos.",
    description: "Centro de eventos, terminal rodoviário e portais de entrada apresentados para Palmas.",
    href: "/grandes-obras",
    mediaSlug: "centro-eventos",
    icon: <Building2 aria-hidden="true" />,
    tone: "royal",
    metrics: [
      { icon: <Users aria-hidden="true" />, text: `${projects.filter((project) => project.area === 'grandes-obras').length} projetos` },
      { icon: <MapPin aria-hidden="true" />, text: "Cultura, serviços e identidade" },
    ],
  },
  {
    slug: "investimento-privado",
    label: "Investimento privado",
    title: "Energia, empregos e desenvolvimento.",
    description: projects.find((project) => project.slug === 'eolico')!.summary,
    href: "/investimentos#eolico",
    mediaSlug: "eolico",
    icon: <Wind aria-hidden="true" />,
    tone: "leaf",
    metrics: [
      { icon: <Wind aria-hidden="true" />, text: `${projects.find((project) => project.slug === 'eolico')!.privateDetails?.turbines?.value} aerogeradores` },
      { icon: <Zap aria-hidden="true" />, text: `${projects.find((project) => project.slug === 'eolico')!.privateDetails?.capacity?.value}` },
    ],
  },
];

const titleLines = ["Quatro frentes", "para Palmas", "avançar."];

export function FourFronts() {
  const section = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const media = gsap.matchMedia();
      media.add("(prefers-reduced-motion: no-preference)", () => {
        const timeline = gsap.timeline({
          defaults: { ease: "power3.out", immediateRender: false },
          onComplete: () => {
            section.current?.querySelectorAll<HTMLElement>("[data-front-card], [data-front-card] .front-card__image img").forEach((element) => {
              element.style.removeProperty('transform');
              element.style.removeProperty('opacity');
              element.style.removeProperty('visibility');
            });
          },
          scrollTrigger: {
            trigger: section.current,
            start: "top 72%",
            once: true,
          },
        });

        timeline
          .from("[data-front-kicker]", { autoAlpha: 0, x: -22, duration: 0.45 })
          .from("[data-type-char]", { autoAlpha: 0, yPercent: 105, filter: "blur(5px)", stagger: 0.022, duration: 0.32 }, 0.08)
          .from("[data-front-intro]", { autoAlpha: 0, y: 24, duration: 0.62 }, 0.34)
          .from("[data-front-card]", { autoAlpha: 0, y: 58, scale: 0.985, stagger: 0.11, duration: 0.78 }, 0.48)
          .from("[data-front-card] .front-card__image img", { scale: 1.09, stagger: 0.11, duration: 1.05 }, 0.48)
          .from("[data-front-footer]", { autoAlpha: 0, y: 26, duration: 0.65 }, 0.92)
          .from("[data-front-caret]", { autoAlpha: 0, duration: 0.1, repeat: 5, yoyo: true }, 0.12)
          .to("[data-front-caret]", { autoAlpha: 0, duration: 0.22 });
      });

      return () => media.revert();
    },
    { scope: section },
  );

  return (
    <section ref={section} className="four-fronts" id="investimentos" aria-labelledby="four-fronts-title">
      <div className="overview-shell">
        <div className="four-fronts__heading">
          <div>
            <p className="overview-kicker" data-front-kicker>Trabalho em todas as áreas</p>
            <h2 id="four-fronts-title" aria-label="Quatro frentes para Palmas avançar">
              {titleLines.map((line, lineIndex) => (
                <span className={lineIndex === 2 ? "four-fronts__green" : ""} key={line} aria-hidden="true">
                  {[...line].map((character, index) => (
                    <i data-type-char key={`${line}-${index}`}>{character === " " ? "\u00a0" : character}</i>
                  ))}
                  {lineIndex === 2 && <b data-front-caret aria-hidden="true" />}
                </span>
              ))}
            </h2>
          </div>
          <div className="four-fronts__intro" data-front-intro>
            <p>As iniciativas abaixo organizam os valores e projetos apresentados para Palmas.</p>
            <div><span><BarIcon /></span><p>Mais resultados<br />para a nossa gente.<br />Hoje e no futuro.</p></div>
          </div>
        </div>

        <div className="front-cards">
          {fronts.map((front) => {
            const media = projects.find((project) => project.slug === front.mediaSlug)!.media[0];
            const total = areaValue(projects, front.slug);
            return (
              <Link
                className={`front-card front-card--${front.tone}`}
                data-front-card
                key={front.slug}
                to={front.href}
                aria-label={`Explorar ${front.label}`}
              >
                <div className="front-card__image"><ResponsiveImage media={media} alt="" sizes="(max-width: 760px) 100vw, (max-width: 1180px) 50vw, 25vw" /></div>
                <div className="front-card__body">
                  <div className="front-card__heading">
                    <span>{front.icon}</span>
                    <small>{front.label}</small>
                  </div>
                  <h3>{front.title}</h3>
                  <p>{front.description}</p>
                  <div className="front-card__value"><small>{total.label}</small><strong>{front.slug === 'investimento-privado' ? projectValue(projects.find((project) => project.slug === front.mediaSlug)!) : formatSummaryCurrency(total.value)}</strong></div>
                  <ul>{front.metrics.map((metric) => <li key={metric.text}>{metric.icon}<span>{metric.text}</span></li>)}</ul>
                  <div className="front-card__action">Explorar {front.label}<ArrowUpRight aria-hidden="true" /></div>
                </div>
              </Link>
            );
          })}
        </div>

        <div className="four-fronts__footer" data-front-footer>
          <span className="four-fronts__quote">“</span>
          <strong>“Mais saúde, mais infraestrutura, mais oportunidades<br />e uma Palmas ainda mais forte.”</strong>
          <p>Juntos por<br />uma Palmas melhor.</p>
          <ResponsiveImage media={advancingSlogan} sizes="220px" />
        </div>
      </div>
    </section>
  );
}

function BarIcon() {
  return <svg viewBox="0 0 48 48" aria-hidden="true"><path d="M10 36V26M23 36V18M36 36V9" /></svg>;
}
